import _ from 'underscore';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { endOfDay, format, isDate, parseISO, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { filterQuery } from '@culture/common-helpers/url';
import { composePath, prepareDateParams } from '@culture/common-helpers/params';

import { useIsMobile } from 'hooks/useIsMobile';
import MobileEventsControls from 'components/filters/MobileEventsControls';
import DesktopEventsControls from 'components/filters/DesktopEventsControls';
import { catalogsActions, catalogsOperations } from 'ducks/catalogs';

const Controls = ({ urlParams, defaultUrlParams = {} }) => {
	const isMobile = useIsMobile();
	const { push, asPath } = useRouter();
	const dispatch = useDispatch();

	const { seanceStartDate = '', seanceEndDate = '' } = urlParams;

	const filters = {
		...urlParams,
		startDate: seanceStartDate && new Date(seanceStartDate),
		endDate: seanceEndDate && new Date(seanceEndDate)
	};

	const getFiltersQueryParams = () =>
		filterQuery(
			prepareDateParams({
				..._(urlParams).omit('seanceStartDate', 'seanceEndDate'),
				startDateFrom: seanceStartDate && startOfDay(parseISO(seanceStartDate)),
				startDateTo: seanceEndDate && endOfDay(parseISO(seanceEndDate))
			})
		);

	const [filtersQueryParams, setFiltersQueryParams] = useState(
		getFiltersQueryParams()
	);

	useEffect(() => {
		const params = _(getFiltersQueryParams()).omit('season');
		setFiltersQueryParams(params);

		dispatch(
			catalogsOperations.getFilterValues(
				'catalogFilters/afisha/special',
				params
			)
		);
	}, [asPath]);

	useEffect(() => () => dispatch(catalogsActions.resetFilterValues()), []);

	const prepareDates = (params, dateNames = []) =>
		_(params).mapObject((val, key) => {
			if (dateNames.includes(key)) {
				const date = !isDate(val) && val ? parseISO(val) : val;

				return date && format(date, 'yyyy-MM-dd');
			}

			return val;
		});

	const changeUrl = ({ minPrice, maxPrice, ...params }) => {
		const { locale, ...modifiedParams } = filterQuery({
			...prepareDates(params, ['seanceStartDate', 'seanceEndDate'])
		});

		const prefix = `/${locale || 'russia'}`;

		const filterParams = {
			..._(modifiedParams).omit('locale'),
			page: null
		};

		const path = composePath(prefix, filterParams);

		push(path, null, { scroll: false });
	};

	const resetFilters = () => {
		changeUrl({});
	};

	const handleFilterChange = useCallback(
		(name, value) => {
			if (!value) {
				const params = {
					..._(urlParams).omit(name),
					page: null
				};
				changeUrl(params);
			}
			if (typeof name === 'string') {
				const currentValue = urlParams[name] || '';
				if (value !== currentValue) {
					const params = {
						...urlParams,
						[name]: value,
						page: null
					};
					changeUrl(params);
				}
			} else if (typeof name === 'object') {
				if (!_.isEqual(name, urlParams)) {
					const params = {
						...urlParams,
						...name,
						page: null
					};
					changeUrl(params);
				}
			}
		},
		[urlParams]
	);

	const handleDatesChange = useCallback(
		(values, newSeason) => {
			const { startDate, endDate, season = '' } = urlParams;

			const isNewSeason = newSeason && newSeason !== season;

			if (!_.isEqual(values, { startDate, endDate })) {
				const params = {
					...urlParams,
					seanceStartDate: values.startDate,
					seanceEndDate: values.endDate,
					season: isNewSeason ? newSeason : null,
					page: null
				};

				changeUrl(params);
			}
		},
		[urlParams]
	);

	if (isMobile) {
		return (
			<MobileEventsControls
				filters={filters}
				onFilterChange={handleFilterChange}
				onDatesChange={handleDatesChange}
				defaultFiltersValues={defaultUrlParams}
				filtersQueryParams={filtersQueryParams}
				resetFilters={resetFilters}
			/>
		);
	}

	return (
		<DesktopEventsControls
			filters={filters}
			onFilterChange={handleFilterChange}
			onDatesChange={handleDatesChange}
			defaultFiltersValues={defaultUrlParams}
			filtersQueryParams={filtersQueryParams}
		/>
	);
};

Controls.propTypes = {
	urlParams: PropTypes.object.isRequired,
	defaultUrlParams: PropTypes.shape({
		locale: PropTypes.string
	})
};

export default Controls;
